import React, { createContext, useContext, ReactNode } from 'react';
import { Typography } from '../../../../core/components/molecules/Typography';
import MemoryButton from '../memory-button';
import { Modal } from '../modal';

interface ModalActionContextProps {
  onClose: () => void;
  onConfirm: () => void;
}

const ModalActionContext = createContext<ModalActionContextProps | null>(null);

const useModalActionContext = () => {
  const context = useContext(ModalActionContext);
  if (!context) {
    throw new Error(
      'MemoryModalAction compound components must be used within MemoryModalAction'
    );
  }
  return context;
};

interface MemoryModalActionProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  children?: ReactNode;
  title?: string | ReactNode;
  description?: string | ReactNode;
}

interface FooterProps {
  children?: ReactNode;
  className?: string;
}

interface BodyProps {
  children?: ReactNode;
}

const Footer = ({ children, className }: FooterProps) => {
  const { onClose, onConfirm } = useModalActionContext();

  if (children) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className='flex flex-row gap-2'>
      <MemoryButton
        label='Cancelar'
        className='w-full'
        variant='outline'
        onClick={onClose}
        allowReadOnly
      />
      <MemoryButton
        label='Sim'
        className='w-full bg-color-brand-primary'
        variant='primary'
        onClick={onConfirm}
      />
    </div>
  );
};

const Body = ({ children }: BodyProps) => {
  return <>{children}</>;
};

const MemoryModalAction = ({
  isOpen,
  onClose,
  onConfirm,
  children,
  title,
  description,
}: MemoryModalActionProps) => {
  const handleConfirm = onConfirm || (() => {});

  const footerChild = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === Footer
  );

  const bodyChild = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === Body
  );

  return (
    <ModalActionContext.Provider value={{ onClose, onConfirm: handleConfirm }}>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={title || ''}
        footer={footerChild || <Footer />}
      >
        <div className='flex flex-col gap-4'>
          <Typography className='text-h4 font-bold text-mono-color-darker'>
            {description}
          </Typography>
          {bodyChild}
        </div>
      </Modal>
    </ModalActionContext.Provider>
  );
};

MemoryModalAction.Footer = Footer;
MemoryModalAction.Body = Body;

export { useModalActionContext };
export default MemoryModalAction;
